import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { galleryImages, typologiesData } from '../data/mock';

const GalleryLightbox = ({ image, onClose, onPrev, onNext }) => (
  <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" data-testid="lightbox">
    <button
      onClick={onClose}
      className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors"
      data-testid="lightbox-close"
    >
      <X className="w-8 h-8" />
    </button>
    
    <button
      onClick={onPrev}
      className="absolute left-6 text-white/60 hover:text-white transition-colors"
      data-testid="lightbox-prev"
    >
      <ChevronLeft className="w-12 h-12" />
    </button>
    
    <button
      onClick={onNext}
      className="absolute right-6 text-white/60 hover:text-white transition-colors"
      data-testid="lightbox-next"
    >
      <ChevronRight className="w-12 h-12" />
    </button>

    <div className="max-w-5xl w-full">
      <img
        src={image.src}
        alt={image.title}
        className="w-full h-auto max-h-[80vh] object-contain"
      />
      <div className="text-center mt-6">
        <span className="text-white/60 text-sm">{image.category}</span>
        <h3 className="text-white text-xl mt-1" style={{ fontFamily: 'Playfair Display, serif' }}>{image.title}</h3>
      </div>
    </div>
  </div>
);

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeCategory, setActiveCategory] = useState('Tous');

  const categories = ['Tous', ...new Set(galleryImages.map(img => img.category))];

  const filteredImages = activeCategory === 'Tous' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  const handlePrevImage = () => {
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    const prevIndex = currentIndex === 0 ? filteredImages.length - 1 : currentIndex - 1;
    setSelectedImage(filteredImages[prevIndex]);
  };

  const handleNextImage = () => {
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    const nextIndex = currentIndex === filteredImages.length - 1 ? 0 : currentIndex + 1;
    setSelectedImage(filteredImages[nextIndex]);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="gallery-page">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-[#1a1a1a]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-white/40 text-sm tracking-[0.2em] uppercase mb-4">
                Nos Résidences
              </p>
              <h1 
                className="text-4xl lg:text-6xl text-white mb-6 leading-tight"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="gallery-title"
              >
                Découvrez <span className="italic">l'excellence</span>
              </h1>
              <p className="text-white/60 text-xl leading-relaxed">
                Explorez les différentes typologies de résidences 
                et laissez-vous séduire par l'art de vivre à Songon.
              </p>
            </div>
          </div>
        </section>

        {/* Typologies Overview */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-8">
              {typologiesData.types.map((type) => (
                <div key={type.id} className="group">
                  <div className="relative overflow-hidden mb-6">
                    <img
                      src={type.image}
                      alt={type.title}
                      className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <p className="text-[#2D5A27] text-sm tracking-wide mb-2">{type.subtitle}</p>
                  <h3 
                    className="text-2xl text-[#1a1a1a] mb-3"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {type.title}
                  </h3>
                  <p className="text-[#666] text-sm leading-relaxed">
                    {type.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section className="py-24 bg-[#F5F5F5]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-4 mb-12 justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-6 py-2 text-sm tracking-wide transition-all duration-300 ${
                    activeCategory === category
                      ? 'bg-[#1a1a1a] text-white'
                      : 'bg-white text-[#666] hover:bg-[#1a1a1a] hover:text-white'
                  }`}
                  data-testid={`category-${category}`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Image Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredImages.map((image) => (
                <div
                  key={image.id}
                  className="group relative aspect-[4/3] overflow-hidden cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                  data-testid={`gallery-image-${image.id}`}
                >
                  <img
                    src={image.src}
                    alt={image.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-end">
                    <div className="p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                      <span className="text-white/80 text-sm">{image.category}</span>
                      <h3 className="text-white font-medium">{image.title}</h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Lightbox */}
        {selectedImage && (
          <GalleryLightbox
            image={selectedImage}
            onClose={() => setSelectedImage(null)}
            onPrev={handlePrevImage}
            onNext={handleNextImage}
          />
        )}
      </main>
      <Footer />
    </div>
  );
};

export default GalleryPage;
